/**
 * Message ordering differs between the two chat backends:
 * - Legacy Assistants threads arrive newest-first (threads.messages.list
 *   defaults to order: desc).
 * - Responses threads are persisted and returned oldest-first
 *   (SEQUENCE_ASC) in the REST/GraphQL chat payloads.
 * toMessageDto in core-v1-service always stamps metadata.openaiResponseId
 * (possibly ''), so that key identifies which ordering a payload uses.
 */
export const isNewestFirst = (thread: any[]): boolean =>
  thread.length > 0 &&
  !thread.some(
    (message: any) =>
      message?.metadata && 'openaiResponseId' in message.metadata
  );

/**
 * Fallback shown when an AI message's content cannot be recovered at all.
 * Without it the row renders as nothing (Message.vue hides empty content),
 * which reads as "the AI never replied" (BIO-2090).
 */
export const EMPTY_AI_MESSAGE_FALLBACK =
  'Sorry, something went wrong. Please try again.';

const KNOWN_TEXT_KEYS = [
  'message',
  'content',
  'question',
  'affirmation',
  'context',
];

/** First readable string value in an unrecognized JSON payload, if any. */
const firstStringValue = (value: any): string => {
  if (typeof value === 'string') {
    return value.trim() ? value : '';
  }
  if (!value || typeof value !== 'object') {
    return '';
  }

  const children = Array.isArray(value) ? value : Object.values(value);

  return children.map(firstStringValue).find((found: string) => found) ?? '';
};

/**
 * Resolve the display text out of a JSON-shaped assistant payload.
 * Returns the key the text came from so the caller can clear that field and
 * avoid rendering the same text twice (affirmation and context have their
 * own dedicated slots in Message.vue).
 */
const resolveJsonContent = (
  parsedMessage: Record<string, unknown>
): { content: string; key?: string } => {
  const key = KNOWN_TEXT_KEYS.find((candidate) => {
    const value = parsedMessage[candidate];

    return typeof value === 'string' && value.trim();
  });
  const knownValue = key ? parsedMessage[key] : undefined;

  if (key && typeof knownValue === 'string') {
    return {
      content: knownValue,
      key,
    };
  }

  return { content: firstStringValue(parsedMessage) };
};

export const convertThreadToMessages = (thread: any[] = []) => {
  const messages = thread.map((message: any) => {
    let parsedMessage;
    let isPlainText = false;

    try {
      parsedMessage = JSON.parse(message.content[0].text.value);
    } catch (e) {
      // If JSON parse fails, it's a plain text message
      parsedMessage = message.content[0].text.value;
      isPlainText = true;
    }

    const isAI = message.role === 'assistant';
    // Legacy Assistants artifact rows are posted as user rows with
    // metadata.artifact set (the backend defaults role to 'user'); they must
    // never surface as chat bubbles, so the BIO-2090 fallbacks below are
    // scoped to non-artifact AI rows only.
    const isArtifact = Boolean(message.metadata?.artifact);
    let content: string;
    let consumedKey: string | undefined;

    if (isPlainText) {
      content = parsedMessage; // Use the plain text directly
    } else if (isAI && !isArtifact) {
      ({ content, key: consumedKey } = resolveJsonContent(parsedMessage));
    } else {
      content =
        parsedMessage.message ||
        parsedMessage.content ||
        parsedMessage.question ||
        '';
    }
    // Never render an AI turn as nothing: an empty or unrecognized payload
    // gets a visible fallback instead of a hidden row (BIO-2090).
    if (isAI && !isArtifact && !content) {
      content = EMPTY_AI_MESSAGE_FALLBACK;
    }

    return {
      text: {
        content,
        example: isPlainText ? undefined : parsedMessage.sample,
        affirmation:
          !isPlainText && consumedKey !== 'affirmation'
            ? parsedMessage.affirmation
            : undefined,
        options: isPlainText ? undefined : parsedMessage.options,
        context:
          !isPlainText && consumedKey !== 'context'
            ? parsedMessage.context
            : undefined,
      },
      isAI,
      isHidden: message.metadata.hidden === 'true',
      isArtifact,
      artifactId: message.metadata.value,
    };
  });

  if (isNewestFirst(thread)) {
    messages.reverse();
  }

  return messages;
};
